import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchQbwcConnection } from 'app/services/erp-connection.service';

// SEE-1042 (Req 4) — verify the QBD connection by polling getQbwcConnection
// until the first successful sync. Mirrors the ERPSyncStatusCard poll loop,
// scoped to "wait for first sync". Polls every POLL_MS for VERIFY_WINDOW_MS.
export const POLL_MS = 4000; // ≈3–5s
export const VERIFY_WINDOW_MS = 90000; // ~90s

type VerifyOutcome = 'waiting' | 'success' | 'timeout' | 'failed';

interface QBDVerifyStepProps {
  paidolId: string;
  /** Fired once when the first sync closes successfully. */
  onSuccess: () => void;
  /** Test seams — defaulted to the real cadence in the app. */
  pollMs?: number;
  windowMs?: number;
}

function QBDVerifyStep({
  paidolId,
  onSuccess,
  pollMs = POLL_MS,
  windowMs = VERIFY_WINDOW_MS,
}: QBDVerifyStepProps) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<VerifyOutcome>('waiting');
  const [failureDetail, setFailureDetail] = useState<string | null>(null);
  // Bumped by the "keep polling" affordance to restart the poll window without
  // recreating the connection (Req 4.4).
  const [pollKey, setPollKey] = useState(0);

  // Keep onSuccess in a ref so a new function identity from the parent on each
  // render doesn't tear down and restart the poll loop.
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    let cancelled = false;
    // Holder so the closures below can clear the timers regardless of definition
    // order (assigned once, just below).
    const timers: {
      interval?: ReturnType<typeof setInterval>;
      timeout?: ReturnType<typeof setTimeout>;
    } = {};

    const stop = () => {
      clearInterval(timers.interval);
      clearTimeout(timers.timeout);
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const status = await fetchQbwcConnection(paidolId);
        if (cancelled) return;

        // Success — non-null AND lastSyncOk AND a closed first sync (Req 4.2).
        if (status && status.lastSyncOk === true && status.lastSyncAt) {
          setFailureDetail(null);
          setOutcome('success');
          stop();
          onSuccessRef.current();
          return;
        }

        // The connector ran but the sync errored (Req 4.5) — surface the first
        // failing result's detail, but keep polling (a later poll may succeed).
        if (status && status.lastSyncOk === false) {
          setFailureDetail(status.lastResults?.find((r) => !r.ok)?.detail ?? null);
          setOutcome('failed');
          return;
        }

        // null, or no lastSyncAt yet → still waiting for the first poll (Req 4.3).
        setOutcome((prev) => (prev === 'failed' ? prev : 'waiting'));
      } catch (error) {
        // Transient error — try again on the next tick, but still log it (warn,
        // not error, since a retry is expected) so a persistent network issue is
        // visible instead of silently spinning until the outer timeout fires.
        // eslint-disable-next-line no-console
        console.warn('QBD verify poll failed, retrying next tick:', error);
      }
    };

    setOutcome('waiting');
    setFailureDetail(null);
    timers.interval = setInterval(tick, pollMs);
    timers.timeout = setTimeout(() => {
      if (cancelled) return;
      // Only time out from the waiting state — a terminal 'success' or 'failed'
      // outcome must survive, so we don't clobber a sync's specific failure detail
      // (Req 4.5) with the generic "still waiting" message. (Yash review)
      setOutcome((prev) => (prev === 'waiting' ? 'timeout' : prev));
      clearInterval(timers.interval);
    }, windowMs);
    void tick();

    // Cleanup on unmount, paidol change, and restart (Req 4.6) — no leaked timers.
    return () => {
      cancelled = true;
      stop();
    };
  }, [paidolId, pollKey, pollMs, windowMs]);

  if (outcome === 'success') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="h6">
          {t('erpConnection.qbd.verifySuccessTitle', 'QuickBooks Desktop connected')}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t(
            'erpConnection.qbd.verifySuccessBody',
            'Your first sync completed successfully. Speedchain is now connected to QuickBooks Desktop.'
          )}
        </Typography>
      </Box>
    );
  }

  if (outcome === 'failed') {
    return (
      <Box sx={{ py: 1 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(
            'erpConnection.qbd.verifyFailed',
            'The Web Connector ran but the first sync reported an error. Review the detail below, then keep checking once resolved.'
          )}
        </Alert>
        {failureDetail && (
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mb: 2, wordBreak: 'break-word' }}
          >
            {failureDetail}
          </Typography>
        )}
        <Button variant="outlined" onClick={() => setPollKey((n) => n + 1)}>
          {t('erpConnection.qbd.keepChecking', 'Keep checking')}
        </Button>
      </Box>
    );
  }

  if (outcome === 'timeout') {
    return (
      <Box sx={{ py: 1 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(
            'erpConnection.qbd.verifyTimeout',
            'Still waiting for the first sync. Make sure the QuickBooks Web Connector is running and click "Update Selected" in it, then keep checking.'
          )}
        </Alert>
        <Button variant="outlined" onClick={() => setPollKey((n) => n + 1)}>
          {t('erpConnection.qbd.keepChecking', 'Keep checking')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
      <CircularProgress />
      <Typography variant="body1">
        {t('erpConnection.qbd.verifyWaitingTitle', 'Waiting for the first sync…')}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t(
          'erpConnection.qbd.verifyWaitingBody',
          'In the QuickBooks Web Connector, check Speedchain and click "Update Selected" to run the first sync. This page updates automatically.'
        )}
      </Typography>
    </Box>
  );
}

export default QBDVerifyStep;
